/* eslint-disable complexity */
import {
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef
} from 'react';

import type {Ref} from 'react';
import {GoogleMapsContext, latLngEquals} from '@vis.gl/react-google-maps';

type MarkerEventProps = {
    onClick?: (e: google.maps.MapMouseEvent) => void;
    onDrag?: (e: google.maps.MapMouseEvent) => void;
    onDragStart?: (e: google.maps.MapMouseEvent) => void;
    onDragEnd?: (e: google.maps.MapMouseEvent) => void;
    onMouseOver?: (e: google.maps.MapMouseEvent) => void;
    onMouseOut?: (e: google.maps.MapMouseEvent) => void;
    onPositionChanged?: (p: ReturnType<google.maps.Marker['getPosition']>) => void;
};

export type GmarkerProps = google.maps.MarkerOptions &
    MarkerEventProps & {
        infoWindowContent?: string | HTMLElement | null;
        openInfoWindowOnClick?: boolean;
        pinColor?: string;
    };

export type GmarkerRef = Ref<google.maps.Marker | null>;

export const buildInterestPointGoogleIcon = (color?: string): google.maps.Icon => {
    const fill = color || '#003f4a';
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="34" viewBox="0 0 30 34">
            <path d="M15 2C8.373 2 3 7.373 3 14c0 9.5 12 18 12 18s12-8.5 12-18c0-6.627-5.373-12-12-12z" fill="${fill}" stroke="#ffffff" stroke-width="1.5"/>
            <circle cx="15" cy="14" r="4.5" fill="#ffffff"/>
        </svg>
    `.trim();

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(30, 34),
        anchor: new google.maps.Point(15, 32)
    };
};

/**
 * Monta o HTML exibido dentro do InfoWindow de um Ponto de Interesse.
 * Mantém os mesmos campos exibidos no Popup do Leaflet (nome, descrição, endereço).
 * `whatsappShareUrl`, quando informado, adiciona um link "Compartilhar" — como o
 * InfoWindow é HTML puro (sem handlers React), o compartilhamento vai como link
 * <a> normal, sem precisar de JS.
 */
export const buildInterestPointInfoWindowContent = ({
    name,
    description,
    address,
    whatsappShareUrl
}: {
    name: string;
    description?: string | null;
    address?: string | null;
    whatsappShareUrl?: string | null;
}): string => {
    return `
        <div style="min-width:180px;max-width:240px;font-family:Prometo, sans-serif;">
            <div style="font-weight:bold;font-size:14px;color:#09414A;margin-bottom:4px;">
                ${escapeHtml(name)}
            </div>
            ${
                description
                    ? `<div style="font-size:12px;color:#555;margin-bottom:4px;">${escapeHtml(description)}</div>`
                    : ''
            }
            ${
                address
                    ? `<div style="font-size:11px;color:#777;margin-bottom:4px;">${escapeHtml(address)}</div>`
                    : ''
            }
            ${
                whatsappShareUrl
                    ? `<a href="${whatsappShareUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#25D366;font-weight:bold;text-decoration:none;margin-top:4px;">
                        Compartilhar via WhatsApp
                       </a>`
                    : ''
            }
        </div>
    `.trim();
};

const escapeHtml = (value: string): string => {
    const div = document.createElement('div');
    div.innerText = value;
    return div.innerHTML;
};

function useMarker(props: GmarkerProps) {
    const {
        onClick,
        onDrag,
        onDragStart,
        onDragEnd,
        onMouseOver,
        onMouseOut,
        onPositionChanged,
        position,
        icon,
        pinColor,
        infoWindowContent,
        openInfoWindowOnClick = true,
        ...markerOptions
    } = props;

    const callbacks = useRef<Record<string, (e: unknown) => void>>({});
    Object.assign(callbacks.current, {
        onClick,
        onDrag,
        onDragStart,
        onDragEnd,
        onMouseOver,
        onMouseOut,
        onPositionChanged
    });

    const marker = useRef(new google.maps.Marker()).current;
    const infoWindow = useRef(new google.maps.InfoWindow()).current;

    const resolvedIcon = icon ?? (pinColor ? buildInterestPointGoogleIcon(pinColor) : undefined);

    // note: assim como no Gcircle/Gpolygon, assume-se que setOptions é inteligente o
    // bastante pra não perder tempo atualizando valores que não mudaram
    marker.setOptions({...markerOptions, icon: resolvedIcon});

    useEffect(() => {
        if (!position) return;
        const current = marker.getPosition();
        if (!current || !latLngEquals(position, current)) marker.setPosition(position);
    }, [position, marker]);

    useEffect(() => {
        if (infoWindowContent === undefined || infoWindowContent === null) return;
        infoWindow.setContent(infoWindowContent);
    }, [infoWindowContent, infoWindow]);

    const map = useContext(GoogleMapsContext)?.map;

    // cria a instância do marcador e adiciona ao mapa assim que ele estiver disponível
    useEffect(() => {
        if (!map) {
            if (map === undefined)
                console.error('<Gmarker> has to be inside a Map component.');

            return;
        }

        marker.setMap(map);

        return () => {
            infoWindow.close();
            marker.setMap(null);
        };
    }, [map, marker, infoWindow]);

    // conecta e reconecta os event handlers quando as propriedades relevantes mudam
    useEffect(() => {
        if (!marker) return;

        const gme = google.maps.event;
        [
            ['drag', 'onDrag'],
            ['dragstart', 'onDragStart'],
            ['dragend', 'onDragEnd'],
            ['mouseover', 'onMouseOver'],
            ['mouseout', 'onMouseOut']
        ].forEach(([eventName, eventCallback]) => {
            gme.addListener(marker, eventName, (e: google.maps.MapMouseEvent) => {
                const callback = callbacks.current[eventCallback];
                if (callback) callback(e);
            });
        });

        gme.addListener(marker, 'click', (e: google.maps.MapMouseEvent) => {
            if (openInfoWindowOnClick && infoWindowContent && map) {
                infoWindow.open({map, anchor: marker});
            }
            callbacks.current.onClick?.(e);
        });

        gme.addListener(marker, 'position_changed', () => {
            const newPosition = marker.getPosition();
            callbacks.current.onPositionChanged?.(newPosition);
        });

        return () => {
            gme.clearInstanceListeners(marker);
        };
    }, [marker, infoWindow, map, openInfoWindowOnClick, infoWindowContent]);

    return marker;
}

export const Gmarker = forwardRef((props: GmarkerProps, ref: GmarkerRef) => {
    const marker = useMarker(props);

    useImperativeHandle(ref, () => marker);

    return null;
});
